<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import AgentsAPI from 'dashboard/api/agents/agents';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'select']);
const { t } = useI18n();

const templates = ref([]);
const isLoading = ref(true);

const iconMap = {
  'currency-dollar': 'i-lucide-dollar-sign',
  headphones: 'i-lucide-headphones',
  'trending-up': 'i-lucide-trending-up',
  building: 'i-lucide-building',
  users: 'i-lucide-users',
  sparkles: 'i-lucide-sparkles',
};

onMounted(async () => {
  try {
    const response = await AgentsAPI.getTemplates();
    templates.value = Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to load templates:', error);
    templates.value = [];
  } finally {
    isLoading.value = false;
  }
});

const selectTemplate = templateId => {
  if (props.disabled) return;
  // Clicar de novo desmarca (volta pro estado sem template)
  const next = props.modelValue === templateId ? null : templateId;
  emit('update:modelValue', next);
  emit('select', next ? templates.value.find(tmpl => tmpl.id === next) : null);
};

const getToolsCount = template =>
  Array.isArray(template?.recommended_tools)
    ? template.recommended_tools.length
    : 0;
</script>

<template>
  <div>
    <div v-if="isLoading" class="flex justify-center py-8">
      <Spinner :size="32" />
    </div>

    <div
      v-else
      class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
    >
      <button
        v-for="template in templates"
        :key="template.id"
        type="button"
        class="group relative flex flex-col gap-3 rounded-2xl border p-4 text-left transition-all duration-150"
        :class="[
          modelValue === template.id
            ? 'border-n-teal-8 bg-n-teal-2 ring-1 ring-n-teal-8'
            : 'border-n-weak bg-n-solid-1 hover:border-n-slate-7 hover:bg-n-solid-2',
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        ]"
        :disabled="disabled"
        @click="selectTemplate(template.id)"
      >
        <!-- Ícone + nome na mesma linha: título nunca quebra o card -->
        <div class="flex items-center gap-3 pr-6">
          <span
            class="flex size-10 shrink-0 items-center justify-center rounded-xl"
            :style="{ backgroundColor: `${template.color}20` }"
          >
            <span
              class="size-5"
              :class="iconMap[template.icon] || 'i-lucide-bot'"
              :style="{ color: template.color }"
            />
          </span>
          <h4
            class="min-w-0 text-sm font-semibold leading-snug text-n-slate-12"
          >
            {{ template.name }}
          </h4>
        </div>

        <!-- Check da seleção -->
        <span
          v-if="modelValue === template.id"
          class="absolute right-3 top-3 flex size-5 items-center justify-center rounded-full bg-n-teal-9"
        >
          <span class="i-lucide-check size-3.5 text-white" />
        </span>

        <!-- Altura reservada: todos os cards ficam do mesmo tamanho -->
        <p class="line-clamp-2 min-h-10 text-xs leading-5 text-n-slate-11">
          {{ template.description }}
        </p>

        <!-- Metadados: ferramentas + selos. Proteções vem ligada em TODOS. -->
        <div class="mt-auto flex flex-wrap items-center gap-1.5">
          <span
            class="inline-flex items-center gap-1 rounded-full border border-n-weak px-2 py-0.5 text-[11px] font-medium text-n-slate-11"
          >
            <span class="i-lucide-wrench size-3" />
            {{ getToolsCount(template) }}
            {{ t('AI_AGENTS.TEMPLATES.TOOLS_COUNT') }}
          </span>
          <span
            class="inline-flex items-center gap-1 rounded-full border border-n-violet-6 bg-n-violet-2 px-2 py-0.5 text-[11px] font-medium text-n-violet-11"
            :title="t('AI_AGENTS.TEMPLATES.PROTECTED_HINT')"
          >
            <span class="i-lucide-shield-check size-3" />
            {{ t('AI_AGENTS.TEMPLATES.PROTECTED_BADGE') }}
          </span>
          <span
            v-if="template.is_router"
            class="inline-flex items-center gap-1 rounded-full border border-n-blue-6 bg-n-blue-2 px-2 py-0.5 text-[11px] font-medium text-n-blue-11"
          >
            <span class="i-lucide-shuffle size-3" />
            {{ t('AI_AGENTS.TEMPLATES.ROUTER_BADGE') }}
          </span>
        </div>
      </button>
    </div>
  </div>
</template>
